<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <router-link
          :to="{ name: 'ClientWixtarTicketDetail', params: { ticketId } }"
          class="btn btn-secondary"
        >
          <i class="ri-arrow-left-line"></i>
          返回票券詳情
        </router-link>
      </div>
    </div>

    <div class="page-container">
      <div v-if="ticketLoading" class="loading-state">
        <div class="spinner"></div>
        <p>載入票券資料中...</p>
      </div>

      <div v-else-if="ticketError" class="error-state">
        <p class="error-message">{{ ticketError }}</p>
        <button class="btn btn-secondary" @click="fetchTicket">重試</button>
      </div>

      <template v-else>
        <!-- 票券摘要 -->
        <div class="ticket-summary">
          <div class="ticket-summary-name">{{ ticket?.ticketName || '-' }}</div>
          <div class="ticket-summary-meta">
            <span v-if="ticket?.activitySubject">活動：{{ ticket.activitySubject }}</span>
            <span>票券 ID：{{ ticketId }}</span>
            <span v-if="ticket?.availableQuantity !== null && ticket?.availableQuantity !== undefined">
              Wixtar 剩餘：{{ ticket.availableQuantity }}
            </span>
          </div>
        </div>

        <div v-if="ticket?.status === 'sold_out'" class="alert-block alert-warn">
          <i class="ri-alert-line"></i>
          <span>此票券在 Wixtar 已無剩餘數量，派券將會失敗。</span>
        </div>

        <!-- ============ 已入隊：背景派券進度 ============ -->
        <template v-if="queued">
          <div class="section-card">
            <div class="section-title">
              <i class="ri-send-plane-line"></i>
              派券進行中
            </div>

            <div class="alert-block alert-info">
              <i class="ri-information-line"></i>
              <span>
                派券在背景執行，關閉此頁不會中斷。可隨時到派券紀錄查看結果。
              </span>
            </div>

            <div class="queue-progress">
              <div class="queue-progress-bar">
                <div class="queue-progress-fill" :style="{ width: `${progressPercent}%` }"></div>
              </div>
              <div class="queue-progress-text">
                <span>{{ progressLabel }}</span>
                <span v-if="progress">成功 {{ progress.successCount }} 筆<template v-if="progress.failedCount > 0">、失敗 {{ progress.failedCount }} 筆</template></span>
              </div>
            </div>

            <div class="result-batch">
              批次編號：<span class="cell-code">{{ queued.batchId }}</span>
              <template v-if="queued.invalidCount > 0">
                ・{{ queued.invalidCount }} 位無法派發（查無會員或缺手機）
              </template>
            </div>

            <div v-if="progress?.errorSummary" class="alert-block alert-warn">
              <i class="ri-alert-line"></i>
              <span>{{ progress.errorSummary }}</span>
            </div>

            <div class="form-actions">
              <button class="btn btn-secondary" @click="startNewDispatch">
                <i class="ri-add-line"></i>
                再派一批
              </button>
              <router-link
                :to="{ name: 'ClientWixtarDispatchDetail', params: { batchId: queued.batchId } }"
                class="btn btn-primary"
              >
                <i class="ri-file-list-line"></i>
                查看派券紀錄
              </router-link>
            </div>
          </div>
        </template>

        <!-- ============ 派發後：結果（本機同步模式）============ -->
        <template v-else-if="result">
          <div class="section-card">
            <div class="result-summary" :class="{ 'has-failure': result.failCount > 0 }">
              <span class="result-success">成功 {{ result.successCount }} 筆</span>
              <span v-if="result.failCount > 0" class="result-fail">失敗 {{ result.failCount }} 筆</span>
              <span v-if="result.sendLineNotificationUsed" class="result-notify">
                已推播 {{ result.notificationSentCount }} 筆
              </span>
            </div>

            <div v-if="result.batchId" class="result-batch">
              批次編號：<span class="cell-code">{{ result.batchId }}</span>
            </div>

            <div class="table-wrapper result-table">
              <table>
                <thead>
                  <tr>
                    <th>對象</th>
                    <th>手機</th>
                    <th>結果</th>
                    <th v-if="result.sendLineNotificationUsed">LINE 推播</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in result.results" :key="`${item.memberId || item.phone}-${index}`">
                    <td>{{ item.memberName || (item.memberId ? '（會員）' : '手動輸入') }}</td>
                    <td>{{ item.phone || '-' }}</td>
                    <td>
                      <span :class="item.success ? 'badge badge-success' : 'badge badge-danger'">
                        {{ item.success ? '成功' : '失敗' }}
                      </span>
                      <div v-if="!item.success" class="cell-error">{{ item.message }}</div>
                    </td>
                    <td v-if="result.sendLineNotificationUsed">
                      <template v-if="item.notificationResults?.line?.sent">
                        <span class="badge badge-success">已送出</span>
                      </template>
                      <template v-else-if="item.notificationResults?.line">
                        <span class="badge badge-secondary">未送出</span>
                        <div class="cell-sub">{{ item.notificationResults.line.error }}</div>
                      </template>
                      <span v-else>-</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="form-actions">
              <button class="btn btn-secondary" @click="startNewDispatch">
                <i class="ri-add-line"></i>
                再派一批
              </button>
              <router-link
                v-if="result.batchId"
                :to="{ name: 'ClientWixtarDispatchDetail', params: { batchId: result.batchId } }"
                class="btn btn-primary"
              >
                <i class="ri-file-list-line"></i>
                查看派券紀錄
              </router-link>
            </div>
          </div>
        </template>

        <!-- ============ 派發前：選擇對象與設定 ============ -->
        <template v-else>
          <!-- 派發對象 -->
          <div class="section-card">
            <div class="section-title">
              <i class="ri-user-add-line"></i>
              派發對象
              <span class="section-count">已選 {{ totalTargets }} 個（上限 {{ MAX_TARGETS }}）</span>
            </div>

            <div class="alert-block alert-info">
              <i class="ri-information-line"></i>
              <span>
                派券以手機號碼向 Wixtar 送出，會員必須已在 Wixtar 註冊。
                沒有手機號碼的會員會被跳過並在結果中列出。
              </span>
            </div>

            <!-- 各分頁的選取結果都會累加到同一份已選清單 -->
            <div class="selection-tabs">
              <button
                v-for="tab in selectionTabs"
                :key="tab.id"
                type="button"
                :class="['tab-button', { active: selectionMode === tab.id }]"
                :disabled="submitting"
                @click="selectionMode = tab.id"
              >
                <i :class="tab.icon"></i>
                {{ tab.label }}
              </button>
            </div>

            <div class="selection-content">
              <MemberSearch v-if="selectionMode === 'search'" v-model="selectedMembers" :disabled="submitting" />

              <MemberTagSearch v-else-if="selectionMode === 'tags'" v-model="selectedMembers" :disabled="submitting" />

              <MemberCsvUpload v-else-if="selectionMode === 'csv'" v-model="selectedMembers" :disabled="submitting" />

              <!-- 條件篩選：先解析成具體名單再加入，讓使用者派券前能確認實際對象 -->
              <template v-else-if="selectionMode === 'filter'">
                <div class="filter-sections">
                  <div class="filter-section-card">
                    <div class="filter-section-header" @click="filterSectionsExpanded.tags = !filterSectionsExpanded.tags">
                      <span class="filter-section-title"><i class="ri-price-tag-3-line"></i> 標籤</span>
                      <i :class="filterSectionsExpanded.tags ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
                    </div>
                    <div v-show="filterSectionsExpanded.tags" class="filter-section-body">
                      <TagMatchTypeToggle v-model="filterConfig.tagLogic" />
                      <TagSelectorMultiple v-model="filterConfig.tags" label="包含標籤" />
                      <TagSelectorMultiple v-model="filterConfig.excludeTags" label="排除標籤" />
                    </div>
                  </div>

                  <div class="filter-section-card">
                    <div class="filter-section-header" @click="filterSectionsExpanded.tiers = !filterSectionsExpanded.tiers">
                      <span class="filter-section-title"><i class="ri-vip-crown-line"></i> 會員等級</span>
                      <i :class="filterSectionsExpanded.tiers ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
                    </div>
                    <div v-show="filterSectionsExpanded.tiers" class="filter-section-body">
                      <div v-if="tiersLoading" class="form-text">載入等級中...</div>
                      <div v-else class="tier-selector">
                        <div
                          v-for="tier in availableTiers"
                          :key="tier.id"
                          class="tier-item"
                          :class="{ selected: filterConfig.tiers.includes(tier.id) }"
                          @click="toggleFilterTier(tier.id)"
                        >
                          <span>{{ tier.tierName }}</span>
                          <i v-if="filterConfig.tiers.includes(tier.id)" class="ri-check-line check-icon"></i>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="filter-section-card">
                    <div class="filter-section-header" @click="filterSectionsExpanded.behavior = !filterSectionsExpanded.behavior">
                      <span class="filter-section-title"><i class="ri-shopping-bag-3-line"></i> 消費行為</span>
                      <i :class="filterSectionsExpanded.behavior ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
                    </div>
                    <div v-show="filterSectionsExpanded.behavior" class="filter-section-body">
                      <div class="form-group">
                        <label class="form-label">累積消費金額</label>
                        <div class="range-inputs">
                          <input v-model.number="filterConfig.minTotalSpent" type="number" class="form-control" placeholder="最低" min="0" />
                          <span>～</span>
                          <input v-model.number="filterConfig.maxTotalSpent" type="number" class="form-control" placeholder="最高" min="0" />
                        </div>
                      </div>
                      <div class="form-group">
                        <label class="form-label">消費次數</label>
                        <div class="range-inputs">
                          <input v-model.number="filterConfig.minPurchaseCount" type="number" class="form-control" placeholder="最低" min="0" />
                          <span>～</span>
                          <input v-model.number="filterConfig.maxPurchaseCount" type="number" class="form-control" placeholder="最高" min="0" />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="filter-section-card">
                    <div class="filter-section-header" @click="filterSectionsExpanded.time = !filterSectionsExpanded.time">
                      <span class="filter-section-title"><i class="ri-calendar-line"></i> 時間條件</span>
                      <i :class="filterSectionsExpanded.time ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
                    </div>
                    <div v-show="filterSectionsExpanded.time" class="filter-section-body">
                      <div class="form-group">
                        <label class="form-label">註冊日期</label>
                        <div class="range-inputs">
                          <input v-model="filterConfig.registrationDateFrom" type="date" class="form-control" />
                          <span>～</span>
                          <input v-model="filterConfig.registrationDateTo" type="date" class="form-control" />
                        </div>
                      </div>
                      <div class="form-group">
                        <label class="form-label">距今未消費天數</label>
                        <div class="range-inputs">
                          <input v-model.number="filterConfig.lastPurchaseDaysMin" type="number" class="form-control" placeholder="最少" min="0" />
                          <span>～</span>
                          <input v-model.number="filterConfig.lastPurchaseDaysMax" type="number" class="form-control" placeholder="最多" min="0" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="filter-preview-section">
                  <button type="button" class="btn btn-secondary" :disabled="filtering" @click="runFilter">
                    <i v-if="filtering" class="ri-loader-4-line spin"></i>
                    <i v-else class="ri-search-line"></i>
                    {{ filtering ? '查詢中...' : '查詢符合的會員' }}
                  </button>
                  <button type="button" class="btn btn-link" :disabled="filtering" @click="resetFilter">重設條件</button>
                </div>

                <div v-if="filterResult" class="preview-result-card">
                  <div class="preview-header">
                    <span class="preview-count">
                      符合 {{ filterResult.stats.matched }} 位，可派券 {{ filterResult.stats.dispatchable }} 位
                    </span>
                  </div>

                  <div v-if="filterResult.stats.withoutPhone > 0" class="alert-block alert-warn">
                    <i class="ri-alert-line"></i>
                    <span>
                      其中 {{ filterResult.stats.withoutPhone }} 位未填手機號碼，無法派券，加入時會自動略過。
                    </span>
                  </div>

                  <div v-if="filterResult.stats.exceedsLimit" class="alert-block alert-warn">
                    <i class="ri-alert-line"></i>
                    <span>超過單次派券上限 {{ filterResult.stats.maxTargets }} 位，請縮小條件範圍後分批派發。</span>
                  </div>

                  <div v-if="filterResult.stats.dispatchable === 0" class="preview-empty">
                    沒有可派券的會員。
                  </div>
                  <div v-else class="preview-actions">
                    <button type="button" class="btn btn-primary" @click="applyFilterResult">
                      <i class="ri-user-add-line"></i>
                      加入這 {{ filterResult.stats.dispatchable }} 位
                    </button>
                  </div>
                </div>
              </template>

              <div v-else-if="selectionMode === 'phone'" class="form-group">
                <label class="form-label">直接輸入手機號碼</label>
                <textarea
                  v-model="phoneInput"
                  class="form-control"
                  rows="4"
                  placeholder="一行一個，或以逗號分隔，例如：&#10;0912345678&#10;0987654321"
                  :disabled="submitting"
                ></textarea>
                <small class="form-text">
                  適用於尚未建立 CRM 會員、但已是 Wixtar 會員的對象。
                  若號碼在 CRM 已有對應會員，仍會自動關聯並可收到推播。
                </small>
              </div>
            </div>

            <div v-if="selectedMembers.length" class="selected-chips">
              <div class="selected-chips-header">
                已選 {{ selectedMembers.length }} 位會員
                <button type="button" class="btn btn-sm btn-link" :disabled="submitting" @click="selectedMembers = []">
                  全部清除
                </button>
              </div>
              <div class="chip-list">
                <span v-for="member in selectedMembers" :key="member.id" class="chip">
                  {{ member.name }}
                  <small v-if="member.phone">{{ member.phone }}</small>
                  <small v-else class="chip-warning">無手機</small>
                  <button type="button" :disabled="submitting" @click="removeMember(member.id)">
                    <i class="ri-close-line"></i>
                  </button>
                </span>
              </div>
            </div>

            <div v-if="parsedPhones.length" class="phone-summary">
              已輸入 {{ parsedPhones.length }} 個手機號碼
              <button type="button" class="btn btn-sm btn-link" :disabled="submitting" @click="phoneInput = ''">
                全部清除
              </button>
            </div>
          </div>

          <!-- 派券設定 -->
          <div class="section-card">
            <div class="section-title">
              <i class="ri-settings-3-line"></i>
              派券設定
            </div>

            <div class="form-group">
              <label class="form-label">派券原因</label>
              <input
                v-model="reason"
                type="text"
                class="form-control"
                placeholder="例如：客服補償、活動贈券（會記錄在派券紀錄中）"
                maxlength="200"
                :disabled="submitting"
              />
            </div>

            <label class="notify-toggle">
              <input v-model="sendLineNotification" type="checkbox" :disabled="submitting" />
              <span>派發成功後發送 LINE 個人訊息通知會員</span>
            </label>

            <template v-if="sendLineNotification">
              <div v-if="prechecking" class="alert-block alert-muted">
                <i class="ri-loader-4-line spin"></i>
                <span>檢查推播對象中...</span>
              </div>
              <div
                v-else-if="precheck"
                class="alert-block"
                :class="precheck.notPushableCount > 0 ? 'alert-warn' : 'alert-ok'"
              >
                <i :class="precheck.notPushableCount > 0 ? 'ri-alert-line' : 'ri-checkbox-circle-line'"></i>
                <div>
                  <div>
                    {{ precheck.pushableCount }} 位可收到推播<template v-if="precheck.notPushableCount > 0">，
                    {{ precheck.notPushableCount }} 位收不到（仍會正常派券）</template>
                  </div>
                  <ul v-if="notPushableItems.length" class="precheck-list">
                    <li v-for="item in notPushableItems" :key="item.phone">
                      {{ item.memberName || item.phone }}：{{ item.reason }}
                    </li>
                  </ul>
                </div>
              </div>

              <div class="msg-mode-group">
                <label class="msg-mode-option" :class="{ active: lineMessageMode === 'default' }">
                  <input v-model="lineMessageMode" type="radio" value="default" :disabled="submitting" />
                  <span>
                    <strong>預設票券通知格式</strong>
                    <small>不需選範本，直接推播票券名稱與「查看我的票券」按鈕</small>
                  </span>
                </label>
                <label class="msg-mode-option" :class="{ active: lineMessageMode === 'template' }">
                  <input v-model="lineMessageMode" type="radio" value="template" :disabled="submitting" />
                  <span>
                    <strong>自選個人訊息範本</strong>
                    <small>使用既有的 LINE 個人訊息範本，可帶入會員變數</small>
                  </span>
                </label>
              </div>

              <div class="template-picker-wrapper">
                <div class="picker-preview-layout">
                  <div class="picker-column">
                    <template v-if="lineMessageMode === 'template'">
                      <label class="form-label">個人訊息範本</label>
                      <LineMsgTemplatePicker v-model="selectedTemplate" audience-filter="personal" />
                      <small class="form-text">
                        只列出個人訊息範本；範本可用 <code v-pre>{{ memberName }}</code> 等會員變數。
                      </small>
                    </template>
                    <template v-else>
                      <label class="form-label">通知內容</label>
                      <div class="default-msg-note">
                        <i class="ri-information-line"></i>
                        <div>
                          <div>採用系統固定格式，內容為票券名稱與所屬活動。</div>
                          <div>按鈕會導向該 LINE 官方帳號的會員票券列表。</div>
                        </div>
                      </div>
                    </template>
                  </div>

                  <div class="preview-column">
                    <label class="form-label">
                      訊息預覽
                      <span v-if="lineMessageMode === 'default'" class="preview-type-badge">預設格式</span>
                      <span v-else-if="selectedTemplate" class="preview-type-badge">
                        {{ msgTypeLabel(selectedTemplate.messageType) }}
                      </span>
                    </label>
                    <LineMessagePreview
                      v-bind="previewData"
                      empty-text="選擇範本後即時預覽"
                    />
                    <small v-if="lineMessageMode === 'template'" class="form-text">
                      預覽顯示範本原始內容，實際發送時 <code v-pre>{{ memberName }}</code> 等變數會替換成該會員的資料。
                    </small>
                    <small v-else class="form-text">
                      預覽為示意，實際按鈕連結會依會員綁定的 LINE 官方帳號產生。
                    </small>
                  </div>
                </div>
              </div>
            </template>
          </div>

          <div class="form-actions form-actions-sticky">
            <router-link
              :to="{ name: 'ClientWixtarTicketDetail', params: { ticketId } }"
              class="btn btn-secondary"
            >
              取消
            </router-link>
            <button class="btn btn-primary" :disabled="submitDisabled" @click="handleSubmit">
              <i v-if="submitting" class="ri-loader-4-line spin"></i>
              <i v-else class="ri-send-plane-line"></i>
              {{ submitting ? '派發中...' : `確認派發（${totalTargets}）` }}
            </button>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import MemberSearch from '@/components/MemberSearch.vue';
import MemberTagSearch from '@/components/MemberTagSearch.vue';
import MemberCsvUpload from '@/components/MemberCsvUpload.vue';
import TagSelectorMultiple from '@/components/TagSelectorMultiple.vue';
import TagMatchTypeToggle from '@/components/TagMatchTypeToggle.vue';
import LineMsgTemplatePicker from '@/components/LineMsgTemplatePicker.vue';
import LineMessagePreview from '@/components/LineMessagePreview.vue';
import { wixtarService } from '@/api/modules/wixtar.js';
import { memberTierService } from '@/api/modules/memberTiers.js';
import { showError } from '@/utils/swal.js';
import { toLineMsgTemplatePreviewData, MSG_TYPE_LABEL } from '@/utils/lineMsgTemplatePreview.js';
import { buildWixtarTicketDefaultBubble } from '@/utils/wixtarTicketDefaultMessage.js';

/** 與後端 WixtarTicketDispatchService.MAX_QUEUED_TARGETS 一致 */
const MAX_TARGETS = 1000;

/** 對象變動後延遲預檢，避免每次勾選會員都打一次 API */
const PRECHECK_DEBOUNCE_MS = 400;

/** 背景派券的進度輪詢間隔 */
const PROGRESS_POLL_MS = 3000;

const createFilterConfig = () => ({
  tags: [],
  tagLogic: 'OR',
  excludeTags: [],
  tiers: [],
  minTotalSpent: null,
  maxTotalSpent: null,
  minPurchaseCount: null,
  maxPurchaseCount: null,
  registrationDateFrom: null,
  registrationDateTo: null,
  lastPurchaseDaysMin: null,
  lastPurchaseDaysMax: null,
  status: ['active']
});

export default {
  name: 'WixtarTicketDispatch',
  components: {
    MemberSearch,
    MemberTagSearch,
    MemberCsvUpload,
    TagSelectorMultiple,
    TagMatchTypeToggle,
    LineMsgTemplatePicker,
    LineMessagePreview
  },
  setup() {
    const route = useRoute();
    const { pageTitle } = usePageTitle();

    const ticketId = route.params.ticketId;

    const ticket = ref(null);
    const ticketLoading = ref(false);
    const ticketError = ref('');

    const selectedMembers = ref([]);
    const phoneInput = ref('');
    const reason = ref('');
    const sendLineNotification = ref(false);
    /** 'default' = 預設票券通知格式（不帶範本 ID）、'template' = 自選個人訊息範本 */
    const lineMessageMode = ref('default');
    const selectedTemplate = ref(null);
    const submitting = ref(false);
    const result = ref(null);
    const precheck = ref(null);
    const prechecking = ref(false);

    const selectionMode = ref('search');
    const selectionTabs = [
      { id: 'search', label: '搜尋會員', icon: 'ri-search-line' },
      { id: 'tags', label: '標籤篩選', icon: 'ri-price-tag-3-line' },
      { id: 'filter', label: '條件篩選', icon: 'ri-filter-3-line' },
      { id: 'csv', label: 'CSV 匯入', icon: 'ri-file-upload-line' },
      { id: 'phone', label: '手機號碼', icon: 'ri-phone-line' }
    ];

    const filterConfig = reactive(createFilterConfig());
    const filterSectionsExpanded = reactive({
      tags: true,
      tiers: false,
      behavior: false,
      time: false
    });
    const availableTiers = ref([]);
    const tiersLoading = ref(false);
    const filtering = ref(false);
    const filterResult = ref(null);

    // 背景派券
    const queued = ref(null);
    const progress = ref(null);

    let precheckTimer = null;
    let progressTimer = null;

    const parsedPhones = computed(() => (
      phoneInput.value
        .split(/[\s,，、;；]+/)
        .map((p) => p.trim())
        .filter(Boolean)
    ));

    const totalTargets = computed(() => selectedMembers.value.length + parsedPhones.value.length);

    const notPushableItems = computed(() => (precheck.value?.items || []).filter((i) => !i.pushable));

    const previewData = computed(() => {
      if (lineMessageMode.value === 'default') {
        return {
          messageType: 'flex',
          flexContent: buildWixtarTicketDefaultBubble({
            ticketName: ticket.value?.ticketName,
            activitySubject: ticket.value?.activitySubject
          })
        };
      }
      return toLineMsgTemplatePreviewData(selectedTemplate.value);
    });

    const msgTypeLabel = (type) => MSG_TYPE_LABEL[type] || type;

    // 自選範本模式沒選範本就不能送出；預設格式模式不需選範本
    const submitDisabled = computed(() => (
      submitting.value
      || totalTargets.value === 0
      || (sendLineNotification.value && lineMessageMode.value === 'template' && !selectedTemplate.value)
    ));

    // 直接以網址進入時也要能取得票券名稱與剩餘數量
    const fetchTicket = async (refresh = false) => {
      ticketLoading.value = true;
      ticketError.value = '';

      try {
        const response = await wixtarService.getTicketCatalogItem(ticketId, refresh ? { refresh: true } : {});
        if (response?.success) {
          ticket.value = response.data;
        } else {
          ticketError.value = response?.message || '查詢票券失敗';
        }
      } catch (err) {
        console.error('查詢 Wixtar 票券失敗:', err);
        ticketError.value = err.message || '查詢票券失敗';
      } finally {
        ticketLoading.value = false;
      }
    };

    const runPrecheck = async () => {
      if (!sendLineNotification.value || totalTargets.value === 0) {
        precheck.value = null;
        return;
      }

      prechecking.value = true;
      try {
        const response = await wixtarService.precheckDispatch({
          memberIds: selectedMembers.value.map((m) => m.id),
          phones: parsedPhones.value
        });
        precheck.value = response?.success ? response.data : null;
      } catch (err) {
        // 預檢只是輔助提示，失敗不阻擋派券
        console.error('派券預檢失敗:', err);
        precheck.value = null;
      } finally {
        prechecking.value = false;
      }
    };

    const schedulePrecheck = () => {
      if (precheckTimer) clearTimeout(precheckTimer);
      precheckTimer = setTimeout(runPrecheck, PRECHECK_DEBOUNCE_MS);
    };

    watch([sendLineNotification, selectedMembers, parsedPhones], () => {
      if (!sendLineNotification.value) {
        precheck.value = null;
        return;
      }
      schedulePrecheck();
    }, { deep: true });

    const removeMember = (memberId) => {
      selectedMembers.value = selectedMembers.value.filter((m) => m.id !== memberId);
    };

    const loadTiers = async () => {
      tiersLoading.value = true;
      try {
        const response = await memberTierService.getTiers({
          isActive: true,
          sortBy: 'displayOrder',
          order: 'asc'
        });
        if (response?.success && response.data) {
          availableTiers.value = Array.isArray(response.data) ? response.data : (response.data.items || []);
        }
      } catch (err) {
        console.error('載入會員等級失敗:', err);
      } finally {
        tiersLoading.value = false;
      }
    };

    const toggleFilterTier = (tierId) => {
      const index = filterConfig.tiers.indexOf(tierId);
      if (index === -1) {
        filterConfig.tiers.push(tierId);
      } else {
        filterConfig.tiers.splice(index, 1);
      }
    };

    const resetFilter = () => {
      Object.assign(filterConfig, createFilterConfig());
      filterResult.value = null;
    };

    const runFilter = async () => {
      filtering.value = true;
      filterResult.value = null;

      try {
        // 空值不送出，避免後端把 null 當成有效條件
        const payload = {};
        for (const [key, value] of Object.entries(filterConfig)) {
          const isEmpty = value === null
            || value === ''
            || (Array.isArray(value) && value.length === 0);
          if (!isEmpty) payload[key] = value;
        }

        const response = await wixtarService.filterDispatchTargets(payload);
        if (response?.success) {
          filterResult.value = response.data;
        } else {
          await showError('查詢失敗', response?.message || '篩選會員失敗');
        }
      } catch (err) {
        console.error('篩選派券對象失敗:', err);
        await showError('查詢失敗', err.message || '篩選會員失敗');
      } finally {
        filtering.value = false;
      }
    };

    // 篩選結果併入已選清單（以 ID 去重，同一位會員可能已從其他分頁加入）
    const applyFilterResult = () => {
      const existing = new Set(selectedMembers.value.map((m) => m.id));
      const added = filterResult.value.members
        .filter((m) => !existing.has(m.memberId))
        .map((m) => ({ id: m.memberId, name: m.memberName, phone: m.phone }));

      selectedMembers.value = [...selectedMembers.value, ...added];
      filterResult.value = null;
    };

    const stopProgressPolling = () => {
      if (progressTimer) {
        clearInterval(progressTimer);
        progressTimer = null;
      }
    };

    const fetchProgress = async () => {
      if (!queued.value?.batchId) return;

      try {
        const response = await wixtarService.getDispatchRecord(queued.value.batchId);
        if (!response?.success) return;

        progress.value = response.data;

        // 派券結束就停止輪詢，避免無限打 API
        if (['completed', 'failed', 'cancelled'].includes(response.data.status)) {
          stopProgressPolling();
        }
      } catch (err) {
        // 輪詢失敗不影響已送出的派券，使用者仍可到紀錄頁查看
        console.error('查詢派券進度失敗:', err);
      }
    };

    // 再派一批：清空表單並重新取得剩餘數量（前一批已改變 Wixtar 庫存）
    const startNewDispatch = () => {
      stopProgressPolling();
      selectedMembers.value = [];
      phoneInput.value = '';
      reason.value = '';
      sendLineNotification.value = false;
      lineMessageMode.value = 'default';
      selectedTemplate.value = null;
      precheck.value = null;
      result.value = null;
      queued.value = null;
      progress.value = null;
      resetFilter();
      fetchTicket(true);
    };

    const handleSubmit = async () => {
      if (submitDisabled.value) return;

      if (totalTargets.value > MAX_TARGETS) {
        await showError('派發對象過多', `單次派券上限為 ${MAX_TARGETS} 位，請分批派發。`);
        return;
      }

      submitting.value = true;
      try {
        const response = await wixtarService.dispatchTicket({
          ticketId,
          memberIds: selectedMembers.value.map((m) => m.id),
          phones: parsedPhones.value,
          reason: reason.value.trim() || undefined,
          sendLineNotification: sendLineNotification.value,
          // 預設格式不帶範本 ID，後端會改用固定的票券通知格式
          lineMsgTemplateId: sendLineNotification.value && lineMessageMode.value === 'template'
            ? selectedTemplate.value?.id
            : undefined
        });

        if (!response?.success || !response.data) {
          await showError('派發失敗', response?.message || '派發 Wixtar 票券失敗');
          return;
        }

        // 正式環境入隊背景執行，本機無佇列時後端會降級為同步並直接回傳逐筆結果
        if (response.data.mode === 'queued') {
          queued.value = response.data;
          progress.value = null;
          fetchProgress();
          progressTimer = setInterval(fetchProgress, PROGRESS_POLL_MS);
        } else {
          // 結果表格是否顯示推播欄，取決於這次是否有勾推播
          result.value = { ...response.data, sendLineNotificationUsed: sendLineNotification.value };
        }
      } catch (err) {
        console.error('派發 Wixtar 票券失敗:', err);
        await showError('派發失敗', err.message || '派發 Wixtar 票券失敗');
      } finally {
        submitting.value = false;
      }
    };

    const progressPercent = computed(() => {
      if (!progress.value) return 0;
      const { totalChunks, completedChunks, status } = progress.value;
      if (['completed', 'failed'].includes(status)) return 100;
      if (!totalChunks) return 0;
      return Math.min(100, Math.round((completedChunks / totalChunks) * 100));
    });

    const progressLabel = computed(() => {
      if (!progress.value) return '等待背景任務開始...';

      const { status, successCount, failedCount, totalTargets } = progress.value;
      if (status === 'completed') return '派券完成';
      if (status === 'failed') return '派券失敗';

      const done = (successCount || 0) + (failedCount || 0);
      return `已處理 ${done} / ${totalTargets} 位`;
    });

    onMounted(() => {
      fetchTicket();
      loadTiers();
    });

    onBeforeUnmount(() => {
      stopProgressPolling();
      if (precheckTimer) clearTimeout(precheckTimer);
    });

    return {
      MAX_TARGETS,
      pageTitle,
      ticketId,
      ticket,
      ticketLoading,
      ticketError,
      fetchTicket,
      selectedMembers,
      phoneInput,
      reason,
      sendLineNotification,
      lineMessageMode,
      selectedTemplate,
      submitting,
      result,
      precheck,
      prechecking,
      notPushableItems,
      previewData,
      msgTypeLabel,
      parsedPhones,
      totalTargets,
      submitDisabled,
      removeMember,
      startNewDispatch,
      handleSubmit,
      selectionMode,
      selectionTabs,
      filterConfig,
      filterSectionsExpanded,
      availableTiers,
      tiersLoading,
      filtering,
      filterResult,
      toggleFilterTier,
      resetFilter,
      runFilter,
      applyFilterResult,
      queued,
      progress,
      progressPercent,
      progressLabel
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.ticket-summary {
  padding: 14px 18px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  margin-bottom: 16px;
}

.ticket-summary-name {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}

.ticket-summary-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 12.5px;
  color: #6c757d;
}

.section-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 16px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14.5px;
  font-weight: 600;
  color: #333;
  padding-bottom: 12px;
  border-bottom: 1px solid #e9ecef;
  margin-bottom: 16px;
}

.section-title i {
  color: #1890ff;
}

.section-count {
  margin-left: auto;
  font-size: 12.5px;
  font-weight: 400;
  color: #6c757d;
}

.alert-block {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 12.5px;
  line-height: 1.6;
  margin-bottom: 16px;
}

.alert-block i {
  font-size: 16px;
  flex-shrink: 0;
}

.alert-info {
  background: #e8f4ff;
  border: 1px solid #b3d7ff;
  color: #1a3a52;
}

.alert-info i {
  color: #1890ff;
}

.alert-ok {
  background: #ecfdf5;
  border: 1px solid #bbf7d0;
  color: #14532d;
  margin-top: 12px;
  margin-bottom: 0;
}

.alert-warn {
  background: #fff7ed;
  border: 1px solid #fed7aa;
  color: #7c2d12;
  margin-top: 12px;
  margin-bottom: 0;
}

.alert-muted {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  color: #6c757d;
  margin-top: 12px;
  margin-bottom: 0;
}

/* 票券已售完的警示在區塊外，需保留下方間距 */
.page-container > .alert-warn {
  margin-top: 0;
  margin-bottom: 16px;
}

/* 對象選取分頁 */
.selection-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  border-bottom: 1px solid #e9ecef;
  margin-bottom: 16px;
}

.tab-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  font-size: 13.5px;
  color: #6c757d;
  cursor: pointer;
}

.tab-button:hover:not(:disabled) {
  color: #1890ff;
}

.tab-button.active {
  color: #1890ff;
  border-bottom-color: #1890ff;
  font-weight: 600;
}

.tab-button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.selection-content {
  margin-bottom: 16px;
}

/* 條件篩選：可收合的條件分區 */
.filter-sections {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.filter-section-card {
  border: 1px solid #e9ecef;
  border-radius: 6px;
  overflow: hidden;
}

.filter-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #f8f9fa;
  cursor: pointer;
  font-size: 13.5px;
}

.filter-section-title {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: #495057;
}

.filter-section-title i {
  color: #1890ff;
}

.filter-section-body {
  padding: 14px;
  border-top: 1px solid #e9ecef;
}

.range-inputs {
  display: flex;
  align-items: center;
  gap: 8px;
}

.range-inputs span {
  color: #adb5bd;
  flex-shrink: 0;
}

.tier-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tier-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 12px;
  border: 1px solid #dee2e6;
  border-radius: 999px;
  font-size: 13px;
  color: #495057;
  cursor: pointer;
}

.tier-item:hover {
  border-color: #1890ff;
  color: #1890ff;
}

.tier-item.selected {
  background: #e8f4ff;
  border-color: #1890ff;
  color: #1565a0;
  font-weight: 500;
}

.check-icon {
  font-size: 14px;
}

.filter-preview-section {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
}

.preview-result-card {
  margin-top: 14px;
  padding: 14px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
}

.preview-header {
  margin-bottom: 10px;
}

.preview-count {
  font-size: 13.5px;
  font-weight: 600;
  color: #333;
}

.preview-empty {
  font-size: 13px;
  color: #6c757d;
}

.preview-actions {
  display: flex;
  justify-content: flex-end;
}

.preview-result-card .alert-block {
  margin-top: 0;
  margin-bottom: 10px;
}

.phone-summary {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #495057;
  margin-bottom: 12px;
}

/* 背景派券進度 */
.queue-progress {
  margin-bottom: 14px;
}

.queue-progress-bar {
  height: 8px;
  background: #e9ecef;
  border-radius: 999px;
  overflow: hidden;
}

.queue-progress-fill {
  height: 100%;
  background: #1890ff;
  border-radius: 999px;
  transition: width 0.4s ease;
}

.queue-progress-text {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 6px;
  font-size: 12.5px;
  color: #6c757d;
}

.selected-chips {
  margin: 12px 0 16px;
}

.selected-chips-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  color: #495057;
  margin-bottom: 8px;
}

.chip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px 4px 10px;
  background: #ecfdf5;
  border: 1px solid #bbf7d0;
  border-radius: 999px;
  font-size: 13px;
  color: #14532d;
}

.chip small {
  color: #166534;
  font-size: 11px;
}

.chip small.chip-warning {
  color: #b45309;
  font-weight: 600;
}

.chip button {
  background: none;
  border: none;
  cursor: pointer;
  color: #15803d;
  line-height: 1;
  padding: 0;
}

.notify-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13.5px;
  color: #333;
  cursor: pointer;
}

.notify-toggle input {
  cursor: pointer;
}

.precheck-list {
  margin: 6px 0 0;
  padding-left: 18px;
}

/* 通知內容來源選擇：預設格式或自選範本 */
.msg-mode-group {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 12px;
}

.msg-mode-option {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex: 1 1 260px;
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
}

.msg-mode-option.active {
  border-color: #00b900;
  background: #f4fbf4;
}

.msg-mode-option input {
  margin-top: 3px;
  cursor: pointer;
}

.msg-mode-option strong {
  display: block;
  font-size: 13.5px;
  font-weight: 600;
  color: #333;
}

.msg-mode-option small {
  display: block;
  margin-top: 2px;
  font-size: 12px;
  color: #888;
}

.default-msg-note {
  display: flex;
  gap: 8px;
  padding: 12px;
  border: 1px dashed #d5d5d5;
  border-radius: 8px;
  font-size: 13px;
  color: #555;
  line-height: 1.7;
}

.default-msg-note i {
  color: #1565a0;
}

.template-picker-wrapper {
  margin-top: 16px;
}

/* 範本清單與預覽並排，選了就能立刻看到訊息長相 */
.picker-preview-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 20px;
  align-items: start;
}

.picker-column,
.preview-column {
  min-width: 0;
}

.preview-type-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 8px;
  background: #e8f4ff;
  color: #1565a0;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 500;
}

/* 窄螢幕改為上下排列，預覽不再固定寬度 */
@media (max-width: 992px) {
  .picker-preview-layout {
    grid-template-columns: minmax(0, 1fr);
  }
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
}

.form-actions-sticky {
  position: sticky;
  bottom: 0;
  background: #fff;
  border-top: 1px solid #e9ecef;
  padding: 14px 20px;
  margin: 0 -20px -20px;
  border-radius: 0 0 8px 8px;
}

.result-summary {
  display: flex;
  gap: 16px;
  padding: 12px 16px;
  border-radius: 8px;
  background: #ecfdf5;
  border: 1px solid #bbf7d0;
  margin-bottom: 12px;
  font-weight: 600;
}

.result-summary.has-failure {
  background: #fff7ed;
  border-color: #fed7aa;
}

.result-success {
  color: #15803d;
}

.result-fail {
  color: #b91c1c;
}

.result-notify {
  color: #0369a1;
}

.result-batch {
  margin-bottom: 12px;
  font-size: 12.5px;
  color: #6c757d;
}

.cell-code {
  font-family: 'SFMono-Regular', Consolas, monospace;
  color: #495057;
}

.result-table table {
  width: 100%;
  min-width: 480px;
  border-collapse: collapse;
}

.result-table th,
.result-table td {
  padding: 8px 12px;
  text-align: left;
  border-bottom: 1px solid #e9ecef;
  font-size: 13px;
  vertical-align: top;
}

.result-table thead {
  background: #f8f9fa;
}

.cell-error {
  margin-top: 4px;
  font-size: 12px;
  color: #b91c1c;
  line-height: 1.5;
}

.cell-sub {
  margin-top: 2px;
  font-size: 12px;
  color: #6c757d;
}

.loading-state,
.error-state {
  padding: 48px 20px;
  text-align: center;
  color: #6c757d;
}

.error-message {
  color: #b91c1c;
  margin-bottom: 12px;
}

.spinner {
  width: 32px;
  height: 32px;
  margin: 0 auto 12px;
  border: 3px solid #e9ecef;
  border-top-color: #1890ff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.spin {
  display: inline-block;
  animation: spin 1s linear infinite;
}
</style>
